import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from './company.reducer';
import * as fromApp from '../../../store/app.reducer';
import * as CompanyActions from './company.actions';
import { environment } from '../../../../environments/environment';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom, finalize, exhaustMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { EmployeeModel } from '../../../models/employee.model';
import { PaginationModel } from '../../../models/pagination.model';
import { InvoiceModel } from '../../../models/invoice.model';
import { InviteModel } from "../../../models/invite.model";
import * as AuthActions from '../../../auth/store/auth.actions';
import * as AuthEffects from '../../../auth/store/auth.effects';
import { RegionModel } from "../../../models/region.model";
import { JobPlaceModel } from "../../../models/jobPlace.model";
import { HereSuggesstionModel } from "../../../models/hereSuggesstion.model";
import { HereLocationModel } from '../../../models/hereLocation.model';
import { BolagsverketService } from '../../../services/bolagsverket.service';
import { TranslateService } from '@ngx-translate/core';
import { createData } from '../../assignments/create-assignment/create-object';
import { SnackbarService } from '../../../shared/snackbar/snackbar.service';
import { CustomerModel } from '../../../models/customer.model';
import { LoadingService } from '../../../service/loading-service.service';

@Injectable()
export class CompanyEffects {

    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private router: Router,
        private snackbarService: SnackbarService,
        private store: Store<fromApp.AppState>,
        private bolagsverketService: BolagsverketService,
        private translate: TranslateService,
        private loadingService: LoadingService,
        private authEffects: AuthEffects.AuthEffects,
    ) {
    }

    loadEmployees = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_EMPLOYEES),
            withLatestFrom(this.store.select(state => state.company)),
            switchMap(([action, state]: [CompanyActions.LoadEmployees, State]) => {
                let params = new HttpParams();
                return this.http.get(environment.apiUrl + '/customers/' + action.payload.customerId + '/employees', { params })
                    .pipe(
                        switchMap((resData: { data: EmployeeModel[], pagination: PaginationModel }) => {
                            return of(new CompanyActions.LoadEmployeesSuccess({
                                employees: resData.data,
                                pagination: resData.pagination
                            }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadEmployeesFailed());
                        }),
                    )
            }),
        );
    });

    inviteUsers = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.INVITE_USERS),
            exhaustMap((action: CompanyActions.InviteUsers) => {
                const payload = { data: action.payload.data };
                this.loadingService.showLoader();

                return this.http.post<{ data: InviteModel[] }>(
                    `${environment.apiUrl}/customers/${action.payload.customer}/invites`,
                    payload
                ).pipe(
                    map(resData => {
                        this.snackbarService.showSuccess(
                            this.translate.instant('company.invite.invitations_sent')
                        );
                        return new CompanyActions.InviteUsersSuccess({ invites: resData.data });
                    }),
                    catchError(() => {
                        this.snackbarService.showError(
                            this.translate.instant('company.invite.failed_to_send_invitations')
                        );
                        return of(new CompanyActions.InviteUsersFailed());
                    }),
                    finalize(() => {
                        this.loadingService.hideLoader();
                    })
                );
            })
        );
    });

    deleteEmployee = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.DELETE_EMPLOYEE),
            switchMap((storeData: CompanyActions.DeleteEmployee) => {
                this.loadingService.showLoader();
                return this.http.delete(environment.apiUrl + '/customers/' + storeData.payload.customerId + '/employees/' + storeData.payload.employeeId)
                    .pipe(
                        switchMap((resData: EmployeeModel) => {
                            this.snackbarService.showSuccess(
                                this.translate.instant('company.users.employee_delete_success')
                            );
                            return of(new CompanyActions.DeleteEmployeeSuccess({ employee: resData }));
                        }),
                        catchError(() => {
                            this.snackbarService.showError(
                                this.translate.instant('company.users.failed_to_delete')
                            );
                            return of(new CompanyActions.DeleteEmployeeFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    )
            }),
        );
    });

    updateEmployee = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.UPDATE_EMPLOYEE),
            switchMap((action: CompanyActions.UpdateEmployee) => {
                return this.http.put(environment.apiUrl + '/customers/' + action.payload.customerId + '/employees/' + action.payload.employeeId, {
                    employeeRoleId: action.payload.employeeRoleId
                })
                    .pipe(
                        switchMap((resData: { data: EmployeeModel }) => {
                            this.snackbarService.showSuccess(
                                this.translate.instant('company.users.employee_updated_succesfully')
                            );
                            return of(new CompanyActions.UpdateEmployeeSuccess({ employee: resData.data }));
                        }),
                        catchError(() => {
                            this.snackbarService.showError(
                                this.translate.instant('company.users.failed_to_update')
                            );
                            return of(new CompanyActions.UpdateEmployeeFailed());
                        }),
                    )
            }),
        );
    });

    loadInvoices = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_INVOICES),
            switchMap((action: CompanyActions.LoadInvoices) => {
                let params = new HttpParams();
                params = params.append('page', action.payload.page);
                params = params.append('per_page', 10);
                return this.http.get(environment.apiUrl + '/customers/' + action.payload.customerId + '/invoices', { params })
                    .pipe(
                        switchMap((resData: { data: InvoiceModel[], pagination: PaginationModel }) => {
                            return of(new CompanyActions.LoadInvoicesSuccess({
                                invoices: resData.data,
                                pagination: resData.pagination
                            }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadInvoicesFailed());
                        }),
                    )
            }),
        );
    });

    loadInvoice = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_INVOICE),
            switchMap((storeData: CompanyActions.LoadInvoice) => {
                return this.http.get(environment.apiUrl + '/customers/' + storeData.payload.customerId + '/invoices/' + storeData.payload.invoiceId)
                    .pipe(
                        switchMap((resData: InvoiceModel) => {
                            return of(new CompanyActions.LoadInvoiceSuccess({ invoice: resData }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadInvoiceFailed());
                        }),
                    )
            }),
        );
    });

    inviteSuccess = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.ACCEPT_INVITE),
            switchMap((storeData: CompanyActions.AcceptInvite) => {
                return this.http.get(environment.apiUrl + '/invites/' + storeData.payload.inviteToken + '/accept-invite')
                    .pipe(
                        switchMap((resData: { data: EmployeeModel, jwt: { accessToken: string, expiresIn: number, tokenType: string } }) => {
                            this.authEffects.handleSuccessfulAuth(resData);
                            this.store.dispatch(new AuthActions.Autologin());
                            return [
                                new CompanyActions.AcceptInviteSuccess({ employee: resData.data, jwt: resData.jwt.accessToken }),
                                new AuthActions.LoginSuccess({ employee: resData.data, jwt: resData.jwt }),
                            ];
                        }),
                        filter((state) => state.type == AuthActions.LOGIN_SUCCESS),
                        take(1),
                        tap(() => {
                            this.router.navigate(['/components/assignments']);
                        }),
                        catchError(() => {
                            this.snackbarService.showError("Failed to accept invitation!");
                            this.router.navigate(['/']);
                            return of(new CompanyActions.AcceptInviteFailed());
                        }),
                    )
            }),
        );
    });

    loadRegions = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_REGIONS),
            switchMap((action: CompanyActions.LoadRegions) => {
                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/regions`)
                    .pipe(
                        switchMap((resData: { data: RegionModel[] }) => {
                            return of(new CompanyActions.LoadRegionsSuccess({ regions: resData.data }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadRegionsFailed());
                        }),
                    )
            }),
        );
    });

    addNewRegion = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.ADD_REGION),
            switchMap((action: CompanyActions.AddNewRegion) => {
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/regions`, action.payload.data)
                    .pipe(
                        switchMap((resData: { data: RegionModel }) => {
                            this.snackbarService.showSuccess("New region successfully created!");
                            return of(new CompanyActions.AddNewRegionSuccess({ region: resData.data }));
                        }),
                        catchError(() => {
                            this.snackbarService.showError("Failed to create new region! Try again later");
                            return of(new CompanyActions.AddNewRegionFailed());
                        }),
                    )
            }),
        );
    });

    loadObjects = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_OBJECTS),
            exhaustMap((action: CompanyActions.LoadObjects) => {
                return this.http.get<{ data: JobPlaceModel[] }>(
                    `${environment.apiUrl}/customers/${action.payload.customerId}/job-places`
                ).pipe(
                    map(resData => new CompanyActions.LoadObjectsSuccess({ objects: resData.data })),
                    catchError(() => of(new CompanyActions.LoadObjectsFailed()))
                );
            }),
        );
    });


    loadObject = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_OBJECT),
            switchMap((action: CompanyActions.LoadObject) => {
                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/job-places/${action.payload.objectId}`)
                    .pipe(
                        switchMap((resData: { data: JobPlaceModel }) => {
                            return of(new CompanyActions.LoadObjectSuccess({ object: resData.data }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadObjectFailed());
                        }),
                    )
            }),
        );
    });

    addNewObject = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.ADD_OBJECT),
            switchMap((action: CompanyActions.AddNewObject) => {
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/job-places`, action.payload.data)
                    .pipe(
                        switchMap((resData: { data: JobPlaceModel }) => {
                            this.snackbarService.showSuccess(this.translate.instant('create-assignment.step3.job-location-create-success'));
                            createData.jobPlaceId = resData.data?.id;
                            return of(new CompanyActions.AddNewObjectSuccess({ object: resData.data }));
                        }),
                        catchError(() => {
                            this.snackbarService.showError(this.translate.instant('create-assignment.step3.failed-to-create-job-location'));
                            return of(new CompanyActions.AddNewObjectFailed());
                        }),
                    )
            }),
        );
    });

    addressAutocomplete = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.ADDRESS_AUTOCOMPLETE),
            switchMap((action: CompanyActions.AddressAutocomplete) => {
                return this.http.post(environment.apiUrl + `/here/geocoding/autocomplete`, {
                    address: action.payload.address
                })
                    .pipe(
                        switchMap((resData: { data: HereSuggesstionModel[] }) => {
                            return of(new CompanyActions.AddressAutocompleteSuccess({ hereSuggestions: resData.data }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.AddressAutocompleteFailed());
                        }),
                    )
            }),
        );
    });

    loadHereLocation = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.LOAD_HERE_LOCATION_BY_LOCATION_ID),
            switchMap((action: CompanyActions.LoadHereLocationByLocationId) => {
                return this.http.get(environment.apiUrl + `/here/geocoding/autocomplete/${action.payload.locationId}`)
                    .pipe(
                        switchMap((resData: HereLocationModel) => {
                            return of(new CompanyActions.LoadHereLocationByLocationIdSuccess({
                                hereLocation: resData
                            }));
                        }),
                        catchError(() => {
                            return of(new CompanyActions.LoadHereLocationByLocationIdFailed());
                        }),
                    )
            }),
        );
    });

    searchCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyActions.SEARCH_COMPANY),
            switchMap((action: CompanyActions.SearchCompany) => {
                return this.bolagsverketService.searchCompany(action.payload.query).pipe(
                    map(response => {
                        return new CompanyActions.SearchCompanySuccess({ companies: response.data });
                    }),
                    catchError(error => {
                        let errorMessage = 'Ett fel uppstod vid sökning av företag';  // Default message in Swedish

                        if (error.error?.error) {
                            try {
                                const apiError = JSON.parse(error.error.error.replace('API error: ', ''));
                                if (apiError.detail) {
                                    errorMessage = apiError.detail;
                                } else {
                                    errorMessage = this.translate.instant('onboarding.company-form.no-results');
                                }
                            } catch (e) {
                                // If parsing fails, use the raw error message
                                errorMessage = error.error.error.replace('API error: ', '');
                            }
                        }

                        return of(new CompanyActions.SearchCompanyFailed({
                            error: errorMessage
                        }));
                    })
                );
            })
        )
    );

    updateCustomerProfile = createEffect(() => {
        return this.actions$.pipe(
            ofType(CompanyActions.UPDATE_CUSTOMER_PROFILE),
            switchMap((storeData: CompanyActions.UpdateCustomerProfile) => {
                const formData = new FormData();
                if (storeData.payload.image)
                    formData.append('image', storeData.payload.image);
                return this.http.post(environment.apiUrl + '/customers/' + storeData.payload.customerId + '/update-profile', formData)
                    .pipe(
                        switchMap((resData: { data: CustomerModel }) => {
                            this.snackbarService.showSuccess(this.translate.instant('company.customer-profile-updated'));
                            return of(new CompanyActions.UpdateCustomerProfileSuccess({ customer: resData.data }));
                        }),
                        catchError(error => {
                            this.snackbarService.showError(error.error?.error || this.translate.instant('company.customer-profile-update-failed'));
                            return of(new CompanyActions.UpdateCustomerProfileFailed());
                        }),
                    )
            }),
        );
    });
}